'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';

export function Listbox<T>({
  className,
  placeholder,
  autoFocus,
  'aria-label': ariaLabel,
  children: options,
  ...props
}: {
  className?: string;
  placeholder?: React.ReactNode;
  autoFocus?: boolean;
  'aria-label'?: string;
  children?: React.ReactNode;
} & Omit<Headless.ListboxProps<'div', T>, 'as' | 'multiple'>) {
  return (
    <Headless.Listbox as="div" {...props} multiple={false}>
      <Headless.ListboxButton
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        data-slot="control"
        aria-label={ariaLabel}
        className={clsx([
          className,
          // Basic layout
          'tw:group tw:relative tw:block tw:w-full tw:!mt-3',
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          'tw:before:absolute tw:before:inset-px tw:before:rounded-[calc(theme(borderRadius.lg)-1px)] tw:before:bg-white tw:before:shadow',
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          'tw:dark:before:hidden',
          // Hide default focus styles
          'tw:focus:outline-none',
          // Focus ring
          'tw:after:pointer-events-none tw:after:absolute tw:after:inset-0 tw:after:rounded-lg tw:after:ring-inset tw:after:ring-transparent tw:after:data-[focus]:ring-2 tw:after:data-[focus]:ring-blue-500',
          // Disabled state
          'tw:data-[disabled]:opacity-50 tw:before:data-[disabled]:bg-zinc-950/5 tw:before:data-[disabled]:shadow-none',
        ])}
      >
        <Headless.ListboxSelectedOption
          as="span"
          options={options}
          placeholder={
            placeholder && (
              <span className="tw:block tw:truncate tw:text-zinc-500">{placeholder}</span>
            )
          }
          className={clsx([
            // Basic layout
            'tw:relative tw:block tw:w-full tw:appearance-none tw:rounded-lg tw:py-[calc(theme(spacing[2.5])-1px)] tw:sm:py-[calc(theme(spacing[1.5])-1px)]',
            // Set minimum height for when no value is selected
            'tw:min-h-11 tw:sm:min-h-9',
            // Horizontal padding
            'tw:pl-[calc(theme(spacing[3.5])-1px)] tw:pr-[calc(theme(spacing.7)-1px)] tw:sm:pl-[calc(theme(spacing.3)-1px)]',
            // Typography
            'tw:text-left tw:text-base/6 tw:text-zinc-950 tw:placeholder:text-zinc-500 tw:sm:text-sm/6 tw:dark:text-white tw:forced-colors:text-[CanvasText]',
            // Border
            'tw:border tw:border-zinc-950/10 tw:group-data-[active]:border-zinc-950/20 tw:group-data-[hover]:border-zinc-950/20 tw:dark:border-white/10 tw:dark:group-data-[active]:border-white/20 tw:dark:group-data-[hover]:border-white/20',
            // Background color
            'tw:bg-transparent tw:dark:bg-white/5',
            // Invalid state
            'tw:group-data-[invalid]:border-red-500 tw:group-data-[invalid]:group-data-[hover]:border-red-500 tw:group-data-[invalid]:dark:border-red-600 tw:group-data-[invalid]:data-[hover]:dark:border-red-600',
            // Disabled state
            'tw:group-data-[disabled]:border-zinc-950/20 tw:group-data-[disabled]:opacity-100 tw:group-data-[disabled]:dark:border-white/15 tw:group-data-[disabled]:dark:bg-white/[2.5%] tw:dark:data-[hover]:group-data-[disabled]:border-white/15',
          ])}
        />
        <span className="tw:pointer-events-none tw:absolute tw:inset-y-0 tw:right-0 tw:flex tw:items-center tw:pr-2">
          <svg
            className="tw:size-5 tw:stroke-zinc-500 tw:group-data-[disabled]:stroke-zinc-600 tw:sm:size-4 tw:dark:stroke-zinc-400 tw:forced-colors:stroke-[CanvasText]"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </Headless.ListboxButton>
      <Headless.ListboxOptions
        transition
        anchor="selection start"
        className={clsx(
          // Anchor positioning
          'tw:[--anchor-offset:-1.625rem] tw:[--anchor-padding:theme(spacing.4)] tw:sm:[--anchor-offset:-1.375rem]',
          // Base styles
          'tw:isolate tw:w-max tw:min-w-[calc(var(--button-width)+1.75rem)] tw:select-none tw:scroll-py-1 tw:rounded-xl tw:p-1',
          // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
          'tw:outline tw:outline-1 tw:outline-transparent tw:focus:outline-none',
          // Handle scrolling when menu won't fit in viewport
          'tw:overflow-y-scroll tw:overscroll-contain',
          // Popover background
          'tw:bg-white/75 tw:backdrop-blur-xl tw:dark:bg-zinc-800/75',
          // Shadows
          'tw:shadow-lg tw:ring-1 tw:ring-zinc-950/10 tw:dark:ring-inset tw:dark:ring-white/10',
          // Transitions
          'tw:transition-opacity tw:duration-100 tw:ease-in tw:data-[transition]:pointer-events-none tw:data-[closed]:data-[leave]:opacity-0'
        )}
      >
        {options}
      </Headless.ListboxOptions>
    </Headless.Listbox>
  );
}

export function ListboxOption<T>({
  children,
  className,
  ...props
}: { className?: string; children?: React.ReactNode } & Omit<
  Headless.ListboxOptionProps<'div', T>,
  'as' | 'className'
>) {
  const sharedClasses = clsx(
    // Base
    'tw:flex tw:min-w-0 tw:items-center',
    // Icons
    'tw:[&>[data-slot=icon]]:size-5 tw:[&>[data-slot=icon]]:shrink-0 tw:sm:[&>[data-slot=icon]]:size-4',
    'tw:[&>[data-slot=icon]]:text-zinc-500 tw:[&>[data-slot=icon]]:group-data-[focus]/option:text-white tw:[&>[data-slot=icon]]:dark:text-zinc-400',
    'tw:forced-colors:[&>[data-slot=icon]]:text-[CanvasText] tw:forced-colors:[&>[data-slot=icon]]:group-data-[focus]/option:text-[Canvas]',
    // Avatars
    'tw:[&>[data-slot=avatar]]:-mx-0.5 tw:[&>[data-slot=avatar]]:size-6 tw:sm:[&>[data-slot=avatar]]:size-5'
  );

  return (
    <Headless.ListboxOption as={Fragment} {...props}>
      {({ selectedOption }) => {
        if (selectedOption) {
          return <div className={clsx(className, sharedClasses)}>{children}</div>;
        }

        return (
          <div
            className={clsx(
              // Basic layout
              'tw:group/option tw:grid tw:cursor-default tw:grid-cols-[theme(spacing.5),1fr] tw:items-baseline tw:gap-x-2 tw:rounded-lg tw:py-2.5 tw:pl-2 tw:pr-3.5 tw:sm:grid-cols-[theme(spacing.4),1fr] tw:sm:py-1.5 tw:sm:pl-1.5 tw:sm:pr-3',
              // Typography
              'tw:text-base/6 tw:text-zinc-950 tw:sm:text-sm/6 tw:dark:text-white tw:forced-colors:text-[CanvasText]',
              // Focus
              'tw:outline-none tw:data-[focus]:bg-brand-green-0 tw:data-[focus]:text-white',
              // Forced colors mode
              'tw:forced-color-adjust-none tw:forced-colors:data-[focus]:bg-[Highlight] tw:forced-colors:data-[focus]:text-[HighlightText]',
              // Disabled
              'tw:data-[disabled]:opacity-50'
            )}
          >
            <svg
              className="tw:relative tw:hidden tw:size-5 tw:self-center tw:stroke-current tw:group-data-[selected]/option:inline tw:sm:size-4"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 8.5l3 3L12 4"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className={clsx(className, sharedClasses, 'tw:col-start-2')}>{children}</span>
          </div>
        );
      }}
    </Headless.ListboxOption>
  );
}

export function ListboxLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(className, 'tw:ml-2.5 tw:truncate tw:first:ml-0 tw:sm:ml-2 tw:sm:first:ml-0')}
    />
  );
}

export function ListboxDescription({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'tw:flex tw:flex-1 tw:overflow-hidden tw:text-zinc-500 tw:before:w-2 tw:before:min-w-0 tw:before:shrink tw:group-data-[focus]/option:text-white tw:dark:text-zinc-400'
      )}
    >
      <span className="tw:flex-1 tw:truncate">{children}</span>
    </span>
  );
}
